import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { KeyRound, Check, Loader2 } from 'lucide-react';
import apiService from '../../../services/api';

const ResetPasswordDialog = ({ open, onOpenChange, shopId, adminEmail }) => {
  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    setError('');
    
    if (!passwordData.password) {
      setError('Password is required');
      return;
    }
    if (passwordData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (passwordData.password !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setResetting(true);
    try {
      const response = await apiService.resetAdminPassword(
        shopId,
        passwordData.password,
        passwordData.confirmPassword
      );
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onOpenChange(false);
          setSuccess(false);
          setPasswordData({ password: '', confirmPassword: '' });
        }, 2000);
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setPasswordData({ password: '', confirmPassword: '' });
    setError('');
    setSuccess(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0]">
        <DialogHeader>
          <DialogTitle className="text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reset Admin Password
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            {success 
              ? 'Password has been reset successfully!'
              : `Set a new password for ${adminEmail || 'admin'}`
            }
          </DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="flex justify-center py-4">
            <div className="w-12 h-12 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
              <Check className="w-6 h-6 text-[#4ADE80]" />
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-[#E2E8F0]">New Password</Label>
                <Input
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimum 8 characters"
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#E2E8F0]">Confirm Password</Label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Re-enter password"
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0]"
                />
              </div>
              {error && (
                <p className="text-[#EF4444] text-sm">{error}</p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                disabled={resetting}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
              >
                {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Reset Password
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordDialog;
